import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { __resolveFormatPick } from '../utils/pdfPrint';

/**
 * Global format picker dialog — replaces the blocked native window.confirm
 * used in utils/pdfPrint.js → askPdfOrXlsx().
 *
 * Mounted once at the app root. Listens for a custom DOM event dispatched
 * by askPdfOrXlsx() and resolves the pending request with the user's pick.
 *
 * Why a global mount?  askPdfOrXlsx is called from many components (Trips,
 * Tickets, Daily Operations, …) so centralizing the UI avoids duplicating
 * Dialog state in every screen.
 */
export default function FormatPickerDialog() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener('ask-pdf-format', handler);
    return () => window.removeEventListener('ask-pdf-format', handler);
  }, []);

  const pick = (format) => {
    setOpen(false);
    // Defer resolve to next tick so the dialog close transition
    // doesn't swallow the synchronous window.open() inside openPdfOrXlsx.
    setTimeout(() => __resolveFormatPick(format), 0);
  };

  const cancel = () => {
    setOpen(false);
    setTimeout(() => __resolveFormatPick(null), 0);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) cancel(); }}>
      <DialogContent className="max-w-md" data-testid="format-picker-dialog">
        <DialogHeader>
          <DialogTitle className="text-amber-800 text-right">
            اختر صيغة الطباعة
          </DialogTitle>
          <DialogDescription className="text-right pt-2 text-sm leading-relaxed text-gray-700">
            اختر الصيغة المناسبة:
            <br />
            <span className="block mt-2">📄 <strong>PDF</strong> — للطباعة والمعاينة (تنسيق ثابت)</span>
            <span className="block">📊 <strong>Excel</strong> — للتعديل والتخصيص (XLSX)</span>
          </DialogDescription>
        </DialogHeader>

        <DialogFooter className="gap-2 pt-3 flex-row sm:justify-end">
          <Button
            variant="outline"
            onClick={cancel}
            data-testid="format-picker-cancel-btn"
          >
            إلغاء
          </Button>
          <Button
            onClick={() => pick('xlsx')}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="format-picker-xlsx-btn"
          >
            📊 Excel
          </Button>
          <Button
            onClick={() => pick('pdf')}
            className="bg-sky-600 hover:bg-sky-700 text-white"
            data-testid="format-picker-pdf-btn"
          >
            📄 PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
